import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SectionHeading from "../components/SectionHeading";
import { useBlogs } from "../hooks/useBlogs";
import { staggerVariants, VIEWPORT_ONCE_TIGHT } from "../lib/motion";

const cardVariants = staggerVariants({ y: 20, duration: 0.5, step: 0.05 });

function BlogCard({ post, index }) {
    return (
        <motion.article
            data-testid={`blog-card-${post.slug}`}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            custom={index}
            viewport={VIEWPORT_ONCE_TIGHT}
            className="group rounded-2xl overflow-hidden bg-[#0A0E23] border border-white/5 hover:border-[#A6E3DC]/30 transition"
        >
            <Link to={`/blogs/${post.slug}`} className="block">
                <div className="aspect-[16/10] overflow-hidden">
                    <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                    />
                </div>
                <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                        <span className="text-[#A6E3DC] font-bold tracking-wider uppercase">
                            {post.category}
                        </span>
                        <span>·</span>
                        <span>{post.read_minutes} min read</span>
                    </div>
                    <h3 className="text-lg font-bold text-white tracking-tight leading-snug group-hover:text-[#A6E3DC] transition">
                        {post.title}
                    </h3>
                    <p className="text-slate-400 text-sm mt-3 leading-relaxed line-clamp-3">
                        {post.excerpt}
                    </p>
                    <p className="text-xs text-slate-500 mt-4">
                        {new Date(post.published_at).toLocaleDateString(
                            undefined,
                            {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                            },
                        )}{" "}
                        · {post.author}
                    </p>
                </div>
            </Link>
        </motion.article>
    );
}

export default function BlogsPage() {
    const { posts, loading } = useBlogs();

    return (
        <div data-testid="blogs-page" className="pt-36 pb-20">
            <div className="xpo-container">
                <SectionHeading
                    overline="XPO Insights"
                    title="Research, education, and the discipline that compounds."
                    description="Notes from the XPO desk on macro, MT5 workflows, risk management, and the markets we trade."
                />

                {loading ? (
                    <p className="text-slate-500 mt-12">Loading…</p>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
                        {posts.map((p, i) => (
                            <BlogCard key={p.id} post={p} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
